import { defineStore } from "pinia";
import { i18n } from "@/init/i18n-setup";
import { api } from "@/api";
import { useNotificationsStore } from "@/stores/notifications";
import { useGeneralStore } from "./generalStore";
import { useMergeTagsStore } from "./mergeTags";
import {
  IBlockProperties,
  BlockPropertiesSchema,
} from "@/schemas/apiBlocks/apiBlocks";

interface State {
  blocks: Map<string, IBlockProperties>;
  custom?: string[];
}

const defaultBlocks = new Map();

export const useApiBlocksStore = defineStore("apiBlocks", {
  state: (): State => ({ blocks: defaultBlocks, custom: [] }),

  getters: {
    supportsDynamicBlocks(): boolean {
      const generalStore = useGeneralStore();
      const mergeTagsStore = useMergeTagsStore();
      return (
        (generalStore.emailServiceProvider === "sparkpost" &&
          mergeTagsStore.smartMergeTags.syntax?.start === "{{" &&
          mergeTagsStore.smartMergeTags.syntax?.end === "}}") ||
        generalStore.type === "ecomail"
      );
    },
  },

  actions: {
    setProductBlock() {
      this.blocks.set("product", {
        name: i18n.global.t("blocks.product"),
        pluralName: i18n.global.t("blocks.products"),
        icon: "product",
        blockStructure: {
          image: {
            defaultValue: "*|PRODUCT_IMG_SRC|*",
            label: i18n.global.t("product.parts.image"),
            width: 300,
            align: "center",
          },
          href: {
            defaultValue: "*|PRODUCT_HREF|*",
          },
          title: {
            defaultValue: `*|PRODUCT_TITLE|*`,
            label: i18n.global.t("product.parts.title"),
            "font-size": ["h3", "14px"],
            color: ["h3", "#000000"],
            "font-family": ["h3", "Helvetica, Arial, sans-serif"],
            "font-weight": ["h3", "normal"],
            "font-style": ["h3", "normal"],
            "text-transform": ["h3", "none"],
            "text-decoration": ["h3", "none"],
            padding: "5px 5px 5px 5px",
            align: "center",
          },
          description: {
            defaultValue: `*|PRODUCT_DESC|*`,
            label: i18n.global.t("product.parts.description"),
            "font-size": ["p", "13px"],
            color: ["p", "#000000"],
            "font-family": ["p", "Helvetica, Arial, sans-serif"],
            "font-weight": ["p", "normal"],
            "font-style": ["p", "normal"],
            "text-transform": ["p", "none"],
            "text-decoration": ["p", "none"],
            padding: "5px 5px 5px 5px",
            align: "center",
          },
          "price-before": {
            defaultValue: `*|PRODUCT_PRICE_BEFORE|*`,
            label: i18n.global.t("product.parts.priceBefore"),
            "font-size": ["p", "13px"],
            color: ["p", "#000000"],
            "font-family": ["p", "Helvetica, Arial, sans-serif"],
            "font-weight": ["p", "normal"],
            "font-style": ["p", "normal"],
            "text-transform": ["p", "none"],
            "text-decoration": ["p", "none"],
            padding: "5px 5px 5px 5px",
            align: "center",
          },
          price: {
            defaultValue: `*|PRODUCT_PRICE|*`,
            label: i18n.global.t("product.parts.price"),
            "font-size": ["h2", "18px"],
            color: ["h2", "#000000"],
            "font-family": ["h2", "Helvetica, Arial, sans-serif"],
            "font-weight": ["h2", "normal"],
            "font-style": ["h2", "normal"],
            "text-transform": ["h2", "none"],
            "text-decoration": ["h2", "none"],
            padding: "5px 5px 5px 5px",
            align: "center",
          },
          button: {
            defaultValue: i18n.global.t("product.more"),
            label: i18n.global.t("product.parts.button"),
            align: "center",
            "text-align": ["mj-button", "center"],
            width: ["mj-button", undefined],
            "background-color": ["mj-button", "#e85034"],
            gradient: ["mj-button", ""],
            color: ["mj-button", "#FFFFFF"],
            "font-size": ["mj-button", "13px"],
            "font-family": ["mj-button", "Helvetica, Arial, sans-serif"],
            "font-weight": ["mj-button", "normal"],
            "font-style": ["mj-button", "normal"],
            "text-transform": ["mj-button", "none"],
            "text-decoration": ["mj-button", "none"],
            padding: "5px 5px 5px 5px",
            "border-radius": ["mj-button", "3px"],
            hover: ["mj-button", undefined],
          },
          layout:
            "image:show,title:show,description:show,priceBefore:show,price:show,button:show",
        },
        apiStructure: {
          id: "id",
          value: "name",
          name: "name",
          title: "name",
          description: "description",
          href: "url",
          image: "img_url",
          price_vat: "price_with_vat",
          product_id: "id",
          currency: "currency",
          price_before: "price_before",
          product_feed_id: "product_feed_id",
        },
        itemsURL: api.PRODUCTS,
        feedsURL: api.FEEDS,
        categoriesURL: api.PRODUCT_CATEGORIES,
      });
    },
    setProductsApi(
      name: "FEEDS" | "PRODUCTS" | "PRODUCT_CATEGORIES",
      value: string | undefined
    ) {
      if (name === "FEEDS") {
        //@ts-expect-error
        this.blocks.get("product").feedsURL = value;
      } else if (name === "PRODUCT_CATEGORIES") {
        //@ts-expect-error
        this.blocks.get("product").categoriesURL = value;
      } else {
        //@ts-expect-error
        this.blocks.get("product").itemsURL = value;
      }
    },
    setProductMergetagTags(mergetags: { label: string; value: string }[]) {
      //@ts-expect-error
      this.blocks.get("product").dynamicMergetags = mergetags;
    },
    setCustomBlocks(customBlocks: Record<string, IBlockProperties>) {
      Object.keys(customBlocks).forEach((key) => {
        const { error, data, success } = BlockPropertiesSchema.safeParse(
          customBlocks[key]
        );
        if (!success) {
          console.error("Error while adding custom api block", error.issues);
          const notificationsStore = useNotificationsStore();
          notificationsStore.createNotification({
            text: "Error while adding custom api block",
            type: "error",
          });
          return;
        }
        if (!this.custom) this.custom = [];
        this.custom.push(key);
        this.blocks.set(key, data);
      });
    },
  },
});
